// client/src/components/OpeningHoursEditor.js
import React, { useCallback } from 'react';
import { TimePicker, Checkbox, Row, Col, Typography } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Text } = Typography;

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const format = 'HH:mm'; // Time format

const OpeningHoursEditor = ({ value, onChange }) => {

    // Function to handle changes and call onChange
    const handleHoursChange = useCallback((day, field, timeValue) => {
        // value prop should contain the current state, e.g., { Monday: { opens: '...', closes: '...' }, ... }
        // Ensure value is treated as an object, even if initially null/undefined
        const currentHours = value || {};
        const currentDayHours = currentHours[day] || { opens: '', closes: '' };
        let newDayHours = { ...currentDayHours };

        if (field === 'opens' || field === 'closes') {
            // timeValue is a dayjs object or null from TimePicker
            newDayHours[field] = timeValue ? timeValue.format(format) : '';
        } else if (field === 'isClosed') {
            // timeValue is the boolean checked state from Checkbox
            if (timeValue === true) { // Closing the shop for the day
                newDayHours.opens = '';
                newDayHours.closes = '';
            } else { // Opening the shop for the day (set defaults or keep existing if re-opening)
                 // If re-opening, keep potentially existing times unless they are empty
                 if (!newDayHours.opens && !newDayHours.closes) {
                    newDayHours.opens = '09:00';
                    newDayHours.closes = '18:00';
                }
            }
        }

        const newHours = {
            ...currentHours, // Use the current value from props
            [day]: newDayHours,
        };

        // Call parent onChange with the updated object structure
        onChange(newHours);

    }, [value, onChange]); // Depend on value and onChange

    return (
        <div>
            {daysOfWeek.map((day) => {
                // Derive state directly from the value prop (ensure it's an object)
                const currentHours = value || {};
                const currentDayHours = currentHours[day] || { opens: '', closes: '' };
                const opensTime = currentDayHours.opens;
                const closesTime = currentDayHours.closes;
                // isClosed is true if both opens and closes are empty strings
                const isClosed = !opensTime && !closesTime;

                return (
                    <Row key={day} gutter={16} align="middle" style={{ marginBottom: '10px' }}>
                        <Col xs={24} sm={4} md={3}>
                            <Text strong>{day}:</Text>
                        </Col>
                        <Col xs={10} sm={7} md={7}>
                            <TimePicker
                                format={format}
                                minuteStep={15}
                                // Value comes from prop, parsed to dayjs
                                value={opensTime ? dayjs(opensTime, format) : null}
                                onChange={(time) => handleHoursChange(day, 'opens', time)}
                                disabled={isClosed}
                                placeholder="Öffnet"
                                style={{ width: '100%' }}
                                allowClear={false} // Prevent accidental clearing leading to isClosed=true
                                inputReadOnly
                            />
                        </Col>
                        <Col xs={10} sm={7} md={7}>
                            <TimePicker
                                format={format}
                                minuteStep={15}
                                // Value comes from prop, parsed to dayjs
                                value={closesTime ? dayjs(closesTime, format) : null}
                                onChange={(time) => handleHoursChange(day, 'closes', time)}
                                disabled={isClosed}
                                placeholder="Schließt"
                                style={{ width: '100%' }}
                                allowClear={false} // Prevent accidental clearing
                                inputReadOnly
                            />
                        </Col>
                        <Col xs={4} sm={6} md={7}>
                            <Checkbox
                                // Checked state derived from string values in prop
                                checked={isClosed}
                                onChange={(e) => handleHoursChange(day, 'isClosed', e.target.checked)}
                            >
                                Geschlossen
                            </Checkbox>
                        </Col>
                    </Row>
                );
            })}
        </div>
    );
};

export default OpeningHoursEditor; 